import { GoogleMap, Marker } from "@react-google-maps/api";
import { useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTimezoneSelect } from "react-timezone-select";
import { BackButton } from "../../../components/buttons/backButton";
import { Button } from "../../../components/buttons/button";
import { EmptyState } from "../../../components/empty";
import { EventImage } from "../../../components/event/eventImage";
import { Icon } from "../../../components/icon/icon";
import { RenderRichText } from "../../../components/typography/renderRichText";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useGoogleMaps } from "../../../customeHooks/useGoogleMaps";
import { useHoldTicketsMutation } from "../../../redux/features/event/event.api";
import {
  CreatedEvent,
  CreatedTicket,
} from "../../../redux/features/event/event.types";
import { tzOptions } from "../../../utils/constants";
import { formatDate } from "../../../utils/helpers";
import { TicketPurchase } from "../../../utils/types";
import { ChooseTickets } from "./components/chooseTickets";
import { PreviewOrganizerDetails } from "./components/organizerDetails";
import {
  getEventAttendance,
  shouldShowAttendanceSignal,
} from "./previewAttendance";

const EYEBROW_LABEL_CLASS =
  "w-full font-onest text-[13px] font-medium uppercase leading-[18px] tracking-[0.08em] text-grey-600";

export const PreviewStep = ({
  event,
  tickets,
  ticketsPurchase,
  setTicketsPurchase,
  setStep,
  canBuy,
  canShare = true,
  showBack = false,
  doHoldTicket,
  refetchTickets = () => {},
}: {
  event: CreatedEvent;
  tickets: CreatedTicket[];
  ticketsPurchase: TicketPurchase[];
  setTicketsPurchase: (data: TicketPurchase[]) => void;
  canBuy: boolean;
  canShare?: boolean;
  showBack?: boolean;
  setStep: (data: 1 | 2) => void;
  doHoldTicket?: boolean;
  refetchTickets: () => void;
}) => {
  const [, setSearchParams] = useSearchParams();

  const { isLoaded: isMapsLoaded } = useGoogleMaps();

  const { parseTimezone } = useTimezoneSelect(tzOptions);

  const mapRef = useRef<HTMLDivElement | null>(null);

  const [holdTickets, { isLoading: holdingTickets }] = useHoldTicketsMutation();

  const soldOut = tickets.every(
    (t) => t.quantityType === "limited" && t.quantityAvailable <= 0,
  );
  const attendance = getEventAttendance(event);
  const canShowMap =
    Boolean(event.placeId && event.latitude && event.longitude) &&
    Number.isFinite(Number(event.latitude)) &&
    Number.isFinite(Number(event.longitude));

  const goToPayment = async () => {
    if (
      doHoldTicket &&
      ticketsPurchase.filter((tp) => tp.ticket.quantityType === "limited")
        .length > 0
    ) {
      const resp = await holdTickets({
        ticketSelections: ticketsPurchase
          .filter((tp) => tp.ticket.quantityType === "limited")
          .map((tp) => {
            return {
              eventId: event._id,
              ticketId: tp.ticket._id,
              quantity: tp.quantity,
            };
          }),
      });

      if (resp.data?.status === "success") {
        refetchTickets();
        setStep(2);
        setSearchParams({
          session: resp.data.data.sessionId,
        });
      }
    } else {
      setStep(2);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {showBack && (
        <div className="z-[1] mx-auto w-full max-w-[1070px]">
          <BackButton />
        </div>
      )}

      <div className="relative flex w-full animate-fade-in flex-col gap-8 pb-10 md:flex-row md:flex-wrap md:items-start md:justify-center md:gap-10">
        <div className="flex w-full flex-col gap-[72px] md:max-w-[500px] xl:sticky xl:top-28">
          <EventImage
            dimension={472}
            image={event.image}
            className="shadow-[0px_2px_8px_0px_rgba(1,35,66,0.18),0px_8px_82px_0px_rgba(1,35,66,0.3)]"
          />

          <PreviewOrganizerDetails
            event={event}
            className="hidden lg:flex"
            canShare={canShare}
            showSocials
          />
        </div>

        <div className="flex w-full flex-col gap-8 text-sm text-grey-950 md:max-w-[530px]">
          <div className="flex flex-col gap-12">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <p className="w-full font-dela text-[32px] leading-[38px] tracking-[-0.64px] text-ink">
                    {event?.name ?? ""}
                  </p>

                  {shouldShowAttendanceSignal(attendance.totalPaidTickets) && (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        <span className="relative z-[1] -mr-2 flex h-5 w-5 items-center justify-center rounded-full border-[1.25px] border-[#eff5fa] bg-[#9939ff]">
                          <Icon
                            svg="avatar"
                            width={13}
                            height={13}
                            className="text-white"
                          />
                        </span>
                        <span className="flex h-5 w-5 items-center justify-center rounded-full border-[1.25px] border-[#eff5fa] bg-[#3988ff]">
                          <Icon
                            svg="avatar"
                            width={13}
                            height={13}
                            className="text-white"
                          />
                        </span>
                      </div>
                      <p className="font-onest text-sm font-medium leading-5 text-grey-800">
                        {attendance.totalPaidTickets} people are going
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <div className="flex shrink-0 items-center justify-center rounded-lg border border-grey-200 p-2">
                      <Icon
                        svg="calendar-fold"
                        width={18}
                        height={18}
                        className="text-grey-900"
                      />
                    </div>
                    <p className="flex-1 font-onest text-sm leading-5 text-grey-950">
                      {formatDate(event?.startDate, "ddd, MMMM DD, YYYY", true)}{" "}
                      {formatDate(event?.startDate) ===
                      formatDate(event?.endDate)
                        ? ""
                        : `- ${formatDate(event?.endDate, "ddd, MMMM DD, YYYY", true)}`}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex shrink-0 items-center justify-center rounded-lg border border-grey-200 p-2">
                      <Icon
                        svg="clock"
                        width={18}
                        height={18}
                        className="text-grey-900"
                      />
                    </div>
                    <p className="flex-1 font-onest text-sm leading-5 text-grey-950">
                      {event?.startTime ?? ""} - {event?.endTime ?? ""}
                      {event.timezone
                        ? ` (${parseTimezone(event.timezone)?.abbrev})`
                        : ""}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex shrink-0 items-center justify-center rounded-lg border border-grey-200 p-2">
                      <Icon
                        svg="map-pin"
                        width={18}
                        height={18}
                        className="text-grey-900"
                      />
                    </div>
                    <p className="flex-1 font-onest text-sm leading-5 text-grey-950">
                      {event.hideLocation
                        ? event.placeholderLocation
                        : event.location}
                    </p>

                    {canShowMap && !event.hideLocation && event.type === "physical" ? (
                      <button
                        type="button"
                        className="flex shrink-0 items-center justify-center whitespace-nowrap rounded-full border-[0.5px] border-grey-200 bg-[#f4f7fa]/60 px-[10px] py-2 font-onest text-sm font-medium leading-5 text-grey-950"
                        onClick={() => {
                          mapRef?.current?.scrollIntoView({
                            behavior: "smooth",
                          });
                        }}
                      >
                        View Map
                      </button>
                    ) : undefined}
                  </div>
                </div>
              </div>

              <div className="flex w-full flex-col gap-[18px] rounded-lg border border-grey-100 bg-white/60 px-[14px] pb-5 pt-[14px]">
                <p className={EYEBROW_LABEL_CLASS}>CHOOSE TICKET</p>

                <div className="flex w-full flex-col gap-6">
                  {(tickets?.length ?? 0) > 0 ? (
                    <ChooseTickets
                      tickets={tickets ?? []}
                      ticketsPurchase={ticketsPurchase}
                      setTicketsPurchase={setTicketsPurchase}
                      showControls={true}
                    />
                  ) : (
                    <EmptyState
                      text="No Tickets Available"
                      subtext="There aren't any tickets available for this event. Check back later."
                      image="/assets/images/ticket-glass.png"
                    />
                  )}

                  <Button
                    text={soldOut ? "Sold Out" : "Get a ticket"}
                    type="button"
                    onClick={goToPayment}
                    loading={holdingTickets}
                    disabled={
                      ticketsPurchase.length === 0 ||
                      !canBuy ||
                      holdingTickets ||
                      soldOut
                    }
                    className=""
                    variation={soldOut ? "disabled" : "primary"}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-12">
              <div className="flex flex-col gap-5">
                <p className={EYEBROW_LABEL_CLASS}>About event</p>
                <div className="font-onest text-base leading-7 text-grey-950">
                  <RenderRichText>{event?.description ?? ""}</RenderRichText>
                </div>
              </div>

              {(event.hideLocation || event.location) &&
              event.type === "physical" ? (
                <div className="flex flex-col gap-5" ref={mapRef}>
                  <p className={EYEBROW_LABEL_CLASS}>Location</p>

                  <BgWrapper
                    loading={!event.hideLocation && !isMapsLoaded}
                    loaderHeight={288}
                    className="flex flex-col gap-5 !rounded-none !bg-transparent !p-0"
                  >
                    <div className="flex items-center gap-3">
                      <p className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-grey-200">
                        <Icon
                          svg="map-pin"
                          width={20}
                          height={20}
                          className="text-grey-900"
                        />
                      </p>

                      <p className="flex flex-1 flex-col">
                        <span className="font-onest text-base font-medium leading-6 text-primary">
                          {event.hideLocation
                            ? "Unlock Location After Ticket Purchase"
                            : event.location}
                        </span>
                        {event.hideLocation ? (
                          <span className="font-onest text-sm leading-[normal] text-grey-800">
                            {event.placeholderLocation}
                          </span>
                        ) : undefined}
                      </p>
                    </div>
                    {event.hideLocation ? (
                      <div className="relative h-[220px] w-full overflow-hidden rounded-lg border border-grey-200 bg-[#f3f6f8]">
                        <img
                          alt=""
                          src="/assets/images/map-hidden-bg.svg"
                          className="pointer-events-none absolute left-1/2 top-1/2 h-[340px] w-[666px] max-w-none -translate-x-1/2 -translate-y-1/2 rotate-180"
                        />
                        <span className="absolute left-1/2 top-1/2 flex h-[62px] w-[62px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-grey-800">
                          <Icon
                            svg="lock-closed"
                            width={28}
                            height={28}
                            className="text-white"
                          />
                        </span>
                      </div>
                    ) : canShowMap && isMapsLoaded ? (
                      <GoogleMap
                        zoom={20}
                        center={{
                          lat: Number(event.latitude),
                          lng: Number(event.longitude),
                        }}
                        mapContainerClassName="w-full h-[220px] rounded-lg border border-grey-100"
                        options={{
                          fullscreenControl: false,
                          mapTypeControl: false,
                          streetViewControl: false,
                          rotateControl: false,
                          scaleControl: false,
                          zoomControl: false,
                          disableDefaultUI: true,
                        }}
                      >
                        <Marker
                          position={{
                            lat: Number(event.latitude),
                            lng: Number(event.longitude),
                          }}
                        />
                      </GoogleMap>
                    ) : undefined}
                  </BgWrapper>
                </div>
              ) : undefined}
            </div>
          </div>

          <PreviewOrganizerDetails
            event={event}
            className="flex lg:hidden"
            canShare={canShare}
            showSocials
          />
        </div>
      </div>
    </div>
  );
};
